/* eslint-disable max-params */
import { API_BASE_URL } from '@/utils/getApiBase';
import { request } from '../request';

export function createSubscriptionTransaction(subscription_type: string, amount: number) {
  const url = `${API_BASE_URL}/pay/create-transaction/?subscription_type=${subscription_type}&amount=${amount}`;

  return request<Api.Billing.CreateTransaction>({
    url,
    method: 'get'
  });
}

export function fetchRemainingCredit() {
  return request<Api.Billing.RemainingCredit>({
    url: `${API_BASE_URL}/api/platform/remaining-credit/`,
    method: 'get'
  });
}

export function fetchUnitExchangeRate() {
  // Get the current date and time
  const now = new Date();

  // Round down the time to the start of the current minute
  const roundedTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes());

  // Convert the rounded time to milliseconds since the epoch
  const cacheBuster = roundedTime.getTime();

  return request<Api.Billing.UnitExchangeRate>({
    url: `${API_BASE_URL}/api/data/unit-exchange-rate/?_=${cacheBuster}`,
    method: 'get'
  });
}

export function reviewExchange(
  source_currency: string,
  target_currency: string,
  source_amount: number,
  target_amount: number,
  exchange_rate: number,
  signal?: AbortSignal
) {
  return request<Api.Billing.ReviewExchange>({
    url: `${API_BASE_URL}/api/platform/exchange-review/`,
    method: 'post',
    data: {
      source_currency,
      target_currency,
      source_amount,
      target_amount,
      exchange_rate
    },
    signal
  });
}

export function confirmExchange(token: string) {
  return request<Api.Billing.ConfirmExchange>({
    url: `${API_BASE_URL}/api/platform/exchange-confirm/`,
    method: 'post',
    data: {
      token
    }
  });
}

// Updated fetch function with version parameter
export function fetchReport(params: App.Report.ReportParams = {}, versionId: string = 'v0') {
  // Merge default parameters with provided params
  const defaultParams = {
    version: versionId
  };

  const finalParams = {
    ...defaultParams,
    ...params
  };

  // Construct the query string from the params object
  const queryString = new URLSearchParams(finalParams).toString();

  return request<App.Report.ReportResponse | any>({
    url: `${API_BASE_URL}/api/data/platform/report/?${queryString}`,
    method: 'get'
  });
}

export function fetchReports(page: number = 1, pageSize: number = 10, fresh: boolean = false) {
  let url = `${API_BASE_URL}/api/data/platform/reports/?page=${page}&page_size=${pageSize}`;

  if (fresh) {
    // Get the current date and time
    const now = new Date();

    // Round down the time to the start of the current minute
    const roundedTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes());

    // Convert the rounded time to milliseconds since the epoch
    const cacheBuster = roundedTime.getTime();

    url += `&_=${cacheBuster}`;
  }

  return request<App.Report.PaginatedResponse>({
    url,
    method: 'get'
  });
}

export function fetchPaymentHistory() {
  return request<App.Payment.PaginatedResponse>({
    url: `${API_BASE_URL}/api/data/platform/payments/history/`,
    method: 'get'
  });
}

export function recheckPaymentStatus(params: { id?: number | string; txid?: string }) {
  const { id, txid } = params;

  if (!id && !txid) {
    throw new Error('Either id or txid must be provided');
  }

  const query = new URLSearchParams();
  if (id) {
    query.append('id', String(id));
  }
  if (txid) {
    query.append('txid', txid);
  }

  return request({
    url: `${API_BASE_URL}/api/platform/payments/recheck/?${query.toString()}`,
    method: 'get'
  });
}

/**
 * Fetches the current credit notification settings
 * Creates default settings if none exist for the user
 */
export function getCreditNotificationSettings() {
  return request<Api.Billing.CreditNotificationSettings>({
    url: `${API_BASE_URL}/api/platform/credit-notification-settings/`,
    method: 'get'
  });
}

/**
 * Updates credit notification settings
 * @param payload - Settings to update (partial updates allowed)
 */
export function updateCreditNotificationSettings(
  payload: Api.Billing.UpdateCreditNotificationSettingsPayload
) {
  return request<Api.Billing.CreditNotificationSettings>({
    url: `${API_BASE_URL}/api/platform/credit-notification-settings/`,
    method: 'post',
    data: payload
  });
}

/**
 * Fetch credit package templates
 */
export function fetchCreditPackageTemplates(params?: {
  service?: string;
  sub_service?: string;
  promotional?: boolean;
}) {
  const queryString = params ? new URLSearchParams(params as any).toString() : '';
  return request<Api.Billing.TemplateListResponse>({
    url: `${API_BASE_URL}/pay/data/credit-packages/templates/${queryString ? '?' + queryString : ''}`,
    method: 'get'
  });
}

/**
 * Purchase a credit package
 */
export function purchaseCreditPackage(data: Api.Billing.PurchaseRequest) {
  return request<Api.Billing.PurchaseResponse>({
    url: `${API_BASE_URL}/pay/credit-packages/purchase/`,
    method: 'post',
    data
  });
}

/**
 * Confirm credit payment with OTP
 */
export function confirmCreditPayment(data: Api.Billing.ConfirmPaymentRequest) {
  return request<Api.Billing.ConfirmPaymentResponse>({
    url: `${API_BASE_URL}/pay/credit-packages/confirm-payment/`,
    method: 'post',
    data
  });
}

/**
 * Fetch user's credit packages
 */
export function fetchUserCreditPackages(params?: {
  status?: 'active' | 'expired' | 'exhausted';
  service?: string;
  sub_service?: string;
}) {
  const queryString = params ? new URLSearchParams(params as any).toString() : '';
  return request<Api.Billing.UserPackagesResponse>({
    url: `${API_BASE_URL}/pay/data/credit-packages/user/${queryString ? '?' + queryString : ''}`,
    method: 'get'
  });
}

/**
 * Fetch all credit sources
 */
export function fetchCreditSources(params?: {
  service?: string;
  sub_service?: string;
}) {
  const queryString = params ? new URLSearchParams(params as any).toString() : '';
  return request<Api.Billing.CreditSourcesResponse>({
    url: `${API_BASE_URL}/pay/data/credit-sources/${queryString ? '?' + queryString : ''}`,
    method: 'get'
  });
}
